import { useState, useEffect, useCallback } from "react";
import { Eye, BarChart3, MessageSquare, FileText } from "lucide-react";
import { debounce } from "lodash";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReactPaginate from "react-paginate";
import { getFeedbackList, getProducts, type Product, type Feedback, type FeedbackListResponse, type FeedbackQueryParams } from "@/services/api";
import FeedbackDetailModal from "@/components/FeedbackDetailModal";
import { getIdToken } from "@/lib/auth";
import { getSentimentClasses } from "@/lib/sentiment";

// Custom Form Response interfaces
interface CustomFormQuestion {
  id: string;
  text: string;
  type: 'OPEN_TEXT' | 'MULTIPLE_CHOICE' | 'RATING' | 'YES_NO';
  isRequired: boolean;
  order: number;
  options: Array<{
    id: string;
    text: string;
    value: string;
    order: number;
  }>;
}

interface CustomForm {
  id: string;
  name: string;
  productId: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  product: {
    id: string;
    name: string;
  };
  questions: CustomFormQuestion[];
}

interface FormResponse {
  id: string;
  createdAt: string;
  formId: string;
  formName: string;
  productName: string;
  totalQuestions: number;
  answers: Array<{
    questionId: string;
    questionText: string;
    questionType: string;
    value: string;
    options?: Array<{ id: string; text: string; value: string }>;
    sentimentAnalysis?: string;
    keywords?: string[];
  }>;
}

interface FormResults {
  responses: FormResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Get Firebase ID token
const getAuthToken = async () => {
  return getIdToken();
};

// Helper function to add auth headers
const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

const FeedbackPage = () => {
  // Generic Feedback State
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>("all");
  const [sentiment, setSentiment] = useState<string>("all");
  const [tags, setTags] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  // Custom Forms State
  const [forms, setForms] = useState<CustomForm[]>([]);
  const [formResponses, setFormResponses] = useState<FormResults | null>(null);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<string>("all");
  const [formResponsesPage, setFormResponsesPage] = useState<number>(1);
  const [selectedFormResponse, setSelectedFormResponse] = useState<FormResponse | null>(null);

  const { toast } = useToast();

  // Create a debounced function that updates the debounced search value
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 400),
    []
  );

  // Update debounced search when search input changes
  useEffect(() => {
    debouncedSetSearch(search);
  }, [search, debouncedSetSearch]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedProductId, sentiment, tags, debouncedSearch]);

  useEffect(() => {
    loadProducts();
    loadForms();
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [selectedProductId, sentiment, tags, debouncedSearch, page, pageSize]);

  useEffect(() => {
    fetchFormResponses();
  }, [selectedFormId, formResponsesPage]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsData = await getProducts();
      setProducts(productsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadForms = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/custom-forms`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch custom forms');
      }
      
      const data = await response.json();
      setForms(data);
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast({
        title: "Error",
        description: "Failed to load custom forms",
        variant: "destructive",
      });
    }
  };

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const params: FeedbackQueryParams = {
        product: selectedProductId !== "all" ? selectedProductId : undefined,
        sentiment: sentiment !== "all" ? sentiment : undefined,
        tags: tags !== "all" ? tags : undefined,
        search: debouncedSearch || undefined,
        page,
        pageSize,
      };
      const data: FeedbackListResponse = await getFeedbackList(params);
      setFeedback(data.results);
      setTotal(data.total);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load feedback",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFormResponses = async () => {
    if (selectedFormId === "all") {
      // Fetch all form responses
      try {
        setFormsLoading(true);
        const headers = await getAuthHeaders();
        const response = await fetch(
          `${API_BASE_URL}/custom-form-responses?page=${formResponsesPage}&limit=10`,
          { headers }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch form responses');
        }

        const data = await response.json();
        setFormResponses(data);
      } catch (error) {
        console.error('Error fetching form responses:', error);
        toast({
          title: "Error",
          description: "Failed to load form responses",
          variant: "destructive",
        });
      } finally {
        setFormsLoading(false);
      }
    } else {
      // Fetch responses for specific form
      try {
        setFormsLoading(true);
        const headers = await getAuthHeaders();
        const response = await fetch(
          `${API_BASE_URL}/custom-form-responses/responses/${selectedFormId}?page=${formResponsesPage}&limit=10`,
          { headers }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch form responses');
        }

        const data = await response.json();
        setFormResponses(data);
      } catch (error) {
        console.error('Error fetching form responses:', error);
        toast({
          title: "Error",
          description: "Failed to load form responses",
          variant: "destructive",
        });
      } finally {
        setFormsLoading(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'OPEN_TEXT':
        return <MessageSquare className="h-4 w-4" />;
      case 'MULTIPLE_CHOICE':
        return <BarChart3 className="h-4 w-4" />;
      case 'RATING':
        return <BarChart3 className="h-4 w-4" />;
      case 'YES_NO':
        return <BarChart3 className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const formatAnswer = (answer: any) => {
    switch (answer.questionType) {
      case 'RATING':
        return `${answer.value}/5`;
      case 'MULTIPLE_CHOICE':
        const option = answer.options?.find((opt: any) => opt.value === answer.value);
        return option?.text || answer.value;
      case 'YES_NO':
        return answer.value === 'yes' ? 'Yes' : 'No';
      default:
        return answer.value;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">Loading feedback...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-muted-foreground">
          View and manage all feedback from your products
        </p>
      </div>

      <Tabs defaultValue="generic" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generic" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Generic Feedback
          </TabsTrigger>
          <TabsTrigger value="forms" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Form Responses
          </TabsTrigger>
        </TabsList>

        {/* Generic Feedback Tab */}
        <TabsContent value="generic" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Product</label>
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger>
                      <SelectValue placeholder="All products" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All products</SelectItem>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Sentiment</label>
                  <Select value={sentiment} onValueChange={setSentiment}>
                    <SelectTrigger>
                      <SelectValue placeholder="All sentiments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sentiments</SelectItem>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Tags</label>
                  <Select value={tags} onValueChange={setTags}>
                    <SelectTrigger>
                      <SelectValue placeholder="All tags" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All tags</SelectItem>
                      <SelectItem value="bug">Bug</SelectItem>
                      <SelectItem value="feature">Feature</SelectItem>
                      <SelectItem value="improvement">Improvement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Search</label>
                  <Input
                    placeholder="Search feedback..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback Table */}
          <Card>
            <CardHeader>
              <CardTitle>Feedback ({total} total)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Sentiment</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedback.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.productName}</TableCell>
                      <TableCell className="max-w-md truncate">{item.content}</TableCell>
                      <TableCell>
                        {item.sentiment && (
                          <Badge className={`${getSentimentClasses(item.sentiment)} border`}>
                            {item.sentiment}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {item.tags?.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(item.submittedAt)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedFeedback(item)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="mt-4">
                <ReactPaginate
                  pageCount={Math.ceil(total / pageSize)}
                  pageRangeDisplayed={5}
                  marginPagesDisplayed={2}
                  onPageChange={({ selected }) => setPage(selected + 1)}
                  containerClassName="flex justify-center space-x-2"
                  pageClassName="px-3 py-2 border rounded hover:bg-gray-100"
                  activeClassName="bg-primary text-primary-foreground border-primary"
                  previousClassName="px-3 py-2 border rounded hover:bg-gray-100"
                  nextClassName="px-3 py-2 border rounded hover:bg-gray-100"
                  disabledClassName="opacity-50 pointer-events-none"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Form Responses Tab */}
        <TabsContent value="forms" className="space-y-6">
          {/* Form Filter */}
          <Card>
            <CardHeader>
              <CardTitle>Form Filter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Form</label>
                  <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                    <SelectTrigger>
                      <SelectValue placeholder="All forms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All forms</SelectItem>
                      {forms.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name} ({form.product.name}) - {form.status.toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Responses Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                Form Responses 
                {formResponses && ` (${formResponses.pagination.total} total)`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {formsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-lg">Loading form responses...</div>
                </div>
              ) : formResponses && formResponses.responses.length > 0 ? (
                <>
                                     <Table>
                     <TableHeader>
                       <TableRow>
                         <TableHead>Product</TableHead>
                         <TableHead>Form</TableHead>
                         <TableHead>Completion</TableHead>
                         <TableHead>Submitted</TableHead>
                         <TableHead>Actions</TableHead>
                       </TableRow>
                     </TableHeader>
                     <TableBody>
                       {formResponses.responses.map((response) => (
                         <TableRow key={response.id}>
                           <TableCell className="font-medium">{response.productName}</TableCell>
                           <TableCell>{response.formName}</TableCell>
                           <TableCell>
                             <Badge variant="outline">
                               {response.answers.length}/{response.totalQuestions} answered
                             </Badge>
                           </TableCell>
                           <TableCell>{formatDate(response.createdAt)}</TableCell>
                           <TableCell>
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => setSelectedFormResponse(response)}
                             >
                               <Eye className="h-4 w-4" />
                             </Button>
                           </TableCell>
                         </TableRow>
                       ))}
                     </TableBody>
                   </Table>

                  {/* Pagination */}
                  {formResponses.pagination.totalPages > 1 && (
                    <div className="mt-4">
                      <ReactPaginate
                        pageCount={formResponses.pagination.totalPages}
                        pageRangeDisplayed={5}
                        marginPagesDisplayed={2}
                        onPageChange={({ selected }) => setFormResponsesPage(selected + 1)}
                        containerClassName="flex justify-center space-x-2"
                        pageClassName="px-3 py-2 border rounded hover:bg-gray-100"
                        activeClassName="bg-primary text-primary-foreground border-primary"
                        previousClassName="px-3 py-2 border rounded hover:bg-gray-100"
                        nextClassName="px-3 py-2 border rounded hover:bg-gray-100"
                        disabledClassName="opacity-50 pointer-events-none"
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No form responses yet</h3>
                    <p className="text-muted-foreground">
                      Responses will appear here once users start submitting forms
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generic Feedback Detail Modal */}
      <FeedbackDetailModal
        feedback={selectedFeedback}
        isOpen={!!selectedFeedback}
        onClose={() => setSelectedFeedback(null)}
      />

      {/* Form Response Detail Modal */}
      {selectedFormResponse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-7xl w-[90vw] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Form Response Details</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFormResponse(null)}
              >
                ×
              </Button>
            </div>
            
            <div className="space-y-6">
              {/* Initial Information Section */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Response Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Product</p>
                    <p className="text-base font-semibold text-gray-900">{selectedFormResponse.productName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Form</p>
                    <p className="text-base font-semibold text-gray-900">{selectedFormResponse.formName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Submitted</p>
                    <p className="text-base font-semibold text-gray-900">{formatDate(selectedFormResponse.createdAt)}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-600 mb-1">Response ID</p>
                  <p className="font-mono text-sm bg-white px-3 py-2 rounded border">{selectedFormResponse.id}</p>
                </div>
              </div>
              
              {/* Questions */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Questions & Answers</h3>
                {selectedFormResponse.answers.map((answer, index) => (
                  <Card key={answer.questionId} className="border-l-4 border-l-blue-500">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        {getQuestionTypeIcon(answer.questionType)}
                        <span className="text-lg font-semibold">Question {index + 1}</span>
                      </CardTitle>
                      <p className="text-base text-gray-700 mt-1">{answer.questionText}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 mb-2">Answer:</p>
                        <p className="text-lg font-medium">{formatAnswer(answer)}</p>
                      </div>
                      
                      {(answer.sentimentAnalysis || (answer.keywords && answer.keywords.length > 0)) && (
                        <div className="flex gap-4">
                          {answer.sentimentAnalysis && (
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-600 mb-2">Sentiment Analysis:</p>
                              <Badge className={`${getSentimentClasses(answer.sentimentAnalysis)} border text-sm px-3 py-1`}>
                                {answer.sentimentAnalysis}
                              </Badge>
                            </div>
                          )}
                          
                          {answer.keywords && answer.keywords.length > 0 && (
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-600 mb-2">Keywords:</p>
                              <div className="flex flex-wrap gap-1">
                                {answer.keywords.map((keyword, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {keyword}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
